import { useState } from 'react';
import { useLocation, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Calendar,
  Settings,
  LogOut,
  CircleUserRound,
  ChevronsUpDown,
  PanelLeft,
  FileText,
  Inbox,
  LayoutDashboard,
  Search,
  MessageSquare,
  ClipboardList,
  Phone,
  ListOrdered,
  Megaphone,
  type LucideIcon,
} from 'lucide-react';
import { P, type PermissionString } from '@vagusx/shared';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useCurrentPermissions } from '@/hooks/use-permissions';
import { useLiveCallCount } from '@/hooks/use-live-calls';
import { liveCountLabel } from '@/lib/live-count';
import { NavIconWithCount, NavCountPill } from './nav-count-badge';
import { signOut } from '@/lib/auth-client';
import { hasAnySetupAccess } from '@/lib/setup-nav';
import { openGlobalSearch } from '@/components/custom/global-search-dialog';
import { FeedbackSheet } from '@/components/custom/feedback/feedback-sheet';

interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  /** A live count badged on the entry; shown only above zero. */
  badge?: 'liveCalls';
  /** Shown only to a user who holds it (system admins always). */
  permission?: PermissionString;
}

const navItems: NavItem[] = [
  {
    title: 'Dashboard',
    url: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'Tasks',
    url: '/tasks',
    icon: Inbox,
  },
  {
    title: 'Calls',
    url: '/calls',
    icon: Phone,
    badge: 'liveCalls',
  },
  {
    title: 'Patients',
    url: '/patients',
    icon: Users,
  },
  {
    title: 'Appointments',
    url: '/appointments',
    icon: Calendar,
  },
  {
    title: 'Waitlist',
    url: '/waitlist',
    icon: ListOrdered,
  },
  {
    title: 'Documents',
    url: '/documents',
    icon: FileText,
  },
  {
    title: 'Referrals',
    url: '/referrals',
    icon: ClipboardList,
  },
  {
    // Out of Setup (ADR-318, P10): the front desk works the queue without
    // setup access.
    title: 'Campaigns',
    url: '/campaigns',
    icon: Megaphone,
    permission: P.OUTREACH_VIEW,
  },
];

export function AppSidebar(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const { permissions, isSystemAdmin } = useCurrentPermissions();
  const { state, setOpen, toggleSidebar } = useSidebar();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Calls on the line now, badged on the Calls entry. This is the count's
  // one poller (the Calls page reads the same cache without its own timer),
  // and it lives in the component that is always mounted — on mobile only
  // the sheet's contents unmount — so the badge is current on every page.
  const liveCalls = useLiveCallCount({ poll: true });

  // Hide the Setup entry from users with no settings access. While
  // permissions load, the hook returns an empty set, so the entry stays
  // hidden rather than flash and snap away.
  const showSetup = hasAnySetupAccess(permissions, isSystemAdmin);

  const handleSidebarClick = (e: React.MouseEvent): void => {
    // Expand sidebar when clicking on empty area (not on buttons, links, or other interactive elements)
    if (state === 'collapsed') {
      const target = e.target as HTMLElement;
      // If the target was unmounted from the DOM (e.g., Radix dropdown closing),
      // treat it as interactive to avoid expanding the sidebar on menu item clicks.
      if (!target.isConnected) return;
      const isInteractive = target.closest('button, a, [role="button"], input, [data-interactive]');
      if (!isInteractive) {
        setOpen(true);
      }
    }
  };

  const isActive = (url: string): boolean => {
    if (url === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(url);
  };

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    // `clear()` drops cached data outright instead of just marking it
    // stale. Critical for any query that's tied to the signed-in user
    // (preferences, me, permissions) — if the next user logs in on
    // the same tab, `invalidateQueries()` alone would hand them the
    // prior user's cached payload while a refetch runs in the
    // background.
    queryClient.clear();
    navigate({ to: '/login' });
  };

  const initials = user
    ? `${user.firstName?.charAt(0) ?? ''}${user.lastName?.charAt(0) ?? ''}`.toUpperCase()
    : '?';

  return (
    <Sidebar collapsible="icon" onClick={handleSidebarClick}>
      <SidebarHeader>
        <div className="flex h-10 items-center justify-between px-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <span className="font-semibold text-lg group-data-[collapsible=icon]:hidden">VagusX</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              toggleSidebar();
            }}
          >
            <PanelLeft className="h-4 w-4" />
            <span className="sr-only">Toggle Sidebar</span>
          </Button>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Search"
                  onClick={(e) => {
                    e.stopPropagation();
                    openGlobalSearch();
                  }}
                >
                  <Search className="h-4 w-4" />
                  <span className="flex-1">Search</span>
                  <kbd className="pointer-events-none ml-auto hidden h-5 select-none items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground group-data-[collapsible=icon]:!hidden sm:flex">
                    <span className="text-xs">⌘</span>K
                  </kbd>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {navItems
                .filter(
                  (item) => !item.permission || isSystemAdmin || permissions.has(item.permission)
                )
                .map((item) => {
                  const count = item.badge === 'liveCalls' ? liveCalls : 0;
                  const label = liveCountLabel(count);
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive(item.url)}
                        tooltip={count > 0 ? `${item.title} · ${label} live` : item.title}
                        // Room for the pill, which is absolutely positioned
                        // over the row's right edge (the collapsed rail's
                        // `p-2!` overrides this).
                        className={count > 0 ? 'pr-8' : undefined}
                      >
                        <Link to={item.url}>
                          <NavIconWithCount icon={item.icon} count={count} />
                          {/* Stays the last child span: the button truncates that one. */}
                          <span>
                            {item.title}
                            {count > 0 ? <span className="sr-only">, {label} live</span> : null}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                      <NavCountPill count={count} />
                    </SidebarMenuItem>
                  );
                })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Send feedback"
              onClick={(e) => {
                e.stopPropagation();
                setFeedbackOpen(true);
              }}
            >
              <MessageSquare className="h-4 w-4" />
              <span>Send feedback</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {showSetup && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={isActive('/setup')} tooltip="Setup">
                <Link to="/setup">
                  <Settings className="h-4 w-4" />
                  <span>Setup</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8">
                    {user && <AvatarImage src={`/api/users/${user.id}/avatar`} />}
                    <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user?.firstName} {user?.lastName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuItem asChild className="gap-2 p-2">
                  <Link to="/profile">
                    <CircleUserRound className="h-4 w-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut} className="gap-2 p-2">
                  <LogOut className="h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <FeedbackSheet open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </Sidebar>
  );
}
